import React, { useEffect, useState } from 'react';
import {
  Instagram,
  KeyRound,
  Save,
  Check,
  ShieldCheck,
  AlertTriangle,
  BarChart3,
  Eye,
  EyeOff,
  TrendingUp,
  Clock,
  Hash,
  Heart,
  MessageCircle,
  Bookmark,
} from 'lucide-react';

const STORAGE_KEY = 'zera_instagram_credentials';

interface IgCredentials {
  access_token: string;
  business_account_id: string;
  app_secret: string;
}

const emptyCreds: IgCredentials = {
  access_token: '',
  business_account_id: '',
  app_secret: '',
};

const CREDENTIAL_FIELDS: {
  key: keyof IgCredentials;
  label: string;
  secretName: string;
  placeholder: string;
  hint: string;
}[] = [
  {
    key: 'access_token',
    label: 'Instagram Access Token',
    secretName: 'INSTAGRAM_ACCESS_TOKEN',
    placeholder: 'IGQVJ...long-lived-token',
    hint: 'Long-lived access token from the Instagram Graph API / Meta Business app.',
  },
  {
    key: 'business_account_id',
    label: 'Business Account ID',
    secretName: 'INSTAGRAM_BUSINESS_ACCOUNT_ID',
    placeholder: '17841400000000000',
    hint: 'The Instagram Business / Creator account ID linked to your Facebook Page.',
  },
  {
    key: 'app_secret',
    label: 'App Secret',
    secretName: 'INSTAGRAM_APP_SECRET',
    placeholder: 'abc123...',
    hint: 'The Meta App Secret used to sign and verify Graph API requests.',
  },
];

const InstagramConnect: React.FC = () => {
  const [creds, setCreds] = useState<IgCredentials>(emptyCreds);
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setCreds({ ...emptyCreds, ...JSON.parse(raw) });
    } catch {
      /* ignore */
    }
  }, []);

  const connected = Boolean(
    creds.access_token && creds.business_account_id && creds.app_secret,
  );

  const handleSave = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(creds));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert('Could not save credentials in this browser.');
    }
  };

  const mask = (v: string) => (v ? `${v.slice(0, 4)}••••••${v.slice(-3)}` : '');

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-serif text-2xl text-[#0D1B2A] font-light">
          Step 6 · Analytics &amp; Instagram Connection
        </h3>

        <p className="font-sans text-sm text-slate-600 mt-2 max-w-2xl">
          Connect your Instagram Graph API credentials here to pull live engagement metrics for
          published posts. Add the keys below now or whenever you're ready — analytics activate
          automatically once all three are saved.
        </p>
      </div>

      {/* Connection status banner */}
      <div
        className={`flex items-center gap-3 border p-4 ${
          connected
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-amber-300 bg-amber-50'
        }`}
      >
        {connected ? (
          <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
        )}
        <div>
          <div className="font-sans text-sm font-medium text-slate-800">
            {connected ? 'Instagram credentials saved' : 'Awaiting Instagram credentials'}
          </div>
          <div className="font-sans text-xs text-slate-500 mt-0.5">
            {connected
              ? 'All three keys are stored. Live analytics can be enabled by your developer.'
              : 'Add the three Instagram Graph API keys below to enable live analytics.'}
          </div>
        </div>
      </div>

      {/* Credentials form */}
      <div className="border border-[#C9A961]/30 bg-gradient-to-br from-[#FAF8F3] to-white p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-9 h-9 rounded bg-[#C9A961]/15 border border-[#C9A961]/40 flex items-center justify-center">
            <Instagram className="w-4 h-4 text-[#C9A961]" />
          </div>
          <div>
            <div className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C9A961]">
              Instagram Graph API
            </div>
            <h4 className="font-serif text-lg text-[#0D1B2A]">Connection Credentials</h4>
          </div>
        </div>

        <div className="space-y-5">
          {CREDENTIAL_FIELDS.map((f) => {
            const value = creds[f.key];
            const show = reveal[f.key];
            const filled = Boolean(value);
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-sans text-xs font-medium text-slate-700 flex items-center gap-2">
                    <KeyRound className="w-3.5 h-3.5 text-[#C9A961]" />
                    {f.label}
                  </label>
                  <span
                    className={`font-sans text-[9px] tracking-wider uppercase px-2 py-0.5 rounded border ${
                      filled
                        ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                        : 'border-slate-200 text-slate-400 bg-slate-50'
                    }`}
                  >
                    {filled ? 'Set' : 'Empty'}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full border border-slate-200 px-3 py-2.5 pr-10 font-mono text-sm text-[#0D1B2A] placeholder:text-slate-400 focus:border-[#C9A961] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setReveal({ ...reveal, [f.key]: !show })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#C9A961]"
                  >
                    {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-sans text-[11px] text-slate-400">{f.hint}</span>
                  <code className="font-mono text-[10px] text-slate-400">{f.secretName}</code>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 bg-[#C9A961] text-[#0D1B2A] hover:bg-[#b89651] transition-colors flex items-center gap-2"
          >
            {saved ? <Check className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            {saved ? 'Saved' : 'Save Credentials'}
          </button>
          {connected && (
            <button
              onClick={() => {
                if (!confirm('Clear saved Instagram credentials?')) return;
                localStorage.removeItem(STORAGE_KEY);
                setCreds(emptyCreds);
              }}
              className="font-sans text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500 transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        <p className="mt-4 font-sans text-[11px] text-slate-400 leading-relaxed border-t border-slate-200 pt-4">
          Note: For production, these values should be stored as encrypted server secrets
          (<code className="text-slate-500">INSTAGRAM_ACCESS_TOKEN</code>,{' '}
          <code className="text-slate-500">INSTAGRAM_BUSINESS_ACCOUNT_ID</code>,{' '}
          <code className="text-slate-500">INSTAGRAM_APP_SECRET</code>) so they're never exposed in
          the browser. This form lets you stage them now; your developer can wire the live Graph API
          pull when ready.
        </p>
      </div>

      {/* Analytics preview (locked until connected) */}
      <div className="border border-slate-200 bg-white p-6 relative overflow-hidden">
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 className="w-4 h-4 text-[#C9A961]" />
          <h4 className="font-serif text-lg text-[#0D1B2A]">Engagement Dashboard</h4>
          {!connected && (
            <span className="font-sans text-[9px] tracking-wider uppercase px-2 py-0.5 rounded border border-amber-300 text-amber-700 bg-amber-50">
              Locked
            </span>
          )}
        </div>

        <div className={`grid sm:grid-cols-2 lg:grid-cols-5 gap-3 ${!connected ? 'opacity-40 pointer-events-none' : ''}`}>
          {[
            { label: 'Likes', icon: Heart },
            { label: 'Comments', icon: MessageCircle },
            { label: 'Saves', icon: Bookmark },
            { label: 'Reach', icon: TrendingUp },
            { label: 'Impressions', icon: Eye },
          ].map(({ label, icon: Icon }) => (
            <div key={label} className="border border-slate-200 p-4 text-center">
              <Icon className="w-5 h-5 text-[#C9A961] mx-auto mb-2" />
              <div className="font-serif text-2xl text-[#0D1B2A]">—</div>
              <div className="font-sans text-[10px] tracking-wider uppercase text-slate-400 mt-1">
                {label}
              </div>
            </div>
          ))}
        </div>

        <div className={`grid md:grid-cols-3 gap-4 mt-4 ${!connected ? 'opacity-40 pointer-events-none' : ''}`}>
          {[
            { label: 'Top Post Types', icon: TrendingUp },
            { label: 'Best Posting Times', icon: Clock },
            { label: 'Hashtag Effectiveness', icon: Hash },
          ].map(({ label, icon: Icon }) => (
            <div key={label} className="border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4 text-[#C9A961]" />
                <span className="font-sans text-[11px] tracking-wider uppercase text-slate-500">
                  {label}
                </span>
              </div>
              <p className="font-sans text-xs text-slate-400">
                Insights appear here once published posts have analytics data.
              </p>
            </div>
          ))}
        </div>

        {!connected && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
            <div className="text-center">
              <KeyRound className="w-8 h-8 text-[#C9A961] mx-auto mb-2" />
              <p className="font-sans text-sm text-slate-600">
                Add and save your Instagram credentials above to unlock analytics.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramConnect;
